import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Target } from 'lucide-react';
import { ApiError } from '../api/client';
import {
  getProduktyTipovackaDetail,
  listProduktyTipovacky,
  type ProduktyTipovackaDetail,
  type ProduktyTipovackaItem,
} from '../api/rondo';
import { ServiceBar } from '../shell/ServiceBar';
import { useTabParam } from '../shell/useTabParam';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Button } from '../components/arc/button/button';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import './users-seznam.css';
import './produkty-tipovacka.css';

type SubTab = 'prehled' | 'planovani';

const intFmt = (n: number) => n.toLocaleString('cs-CZ');

function mainPrize(prizeDescription: string | null, prizeTitle: string | null): string | null {
  const first = prizeDescription?.split(/\r?\n/)[0]?.trim();
  if (first) {
    const amount = first.includes(':') ? first.slice(first.lastIndexOf(':') + 1).trim() : first;
    if (amount) return amount;
  }
  return prizeTitle?.trim() || null;
}

const STATUS_LABEL: Record<string, string> = {
  open: 'otevřeno',
  scheduled: 'naplánováno',
  closed: 'uzavřeno',
  settled: 'vyhodnoceno',
};

const LIMIT_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: 'all', label: 'vše' },
];

function statusBadge(status: string | null) {
  if (!status) return <span className="rondo-tip-status">—</span>;
  const key = status.toLowerCase();
  const label = STATUS_LABEL[key] ?? status;
  return <span className="rondo-tip-status" data-status={key}>{label}</span>;
}

function dateTimeFmt(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('cs-CZ', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function ProduktyTipovackaPage() {
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get('tipGameId');
    const n = Number(raw);
    return raw != null && raw !== '' && Number.isFinite(n) ? n : null;
  });

  useEffect(() => {
    const onPop = () => {
      const raw = new URLSearchParams(window.location.search).get('tipGameId');
      const n = Number(raw);
      setSelectedId(raw != null && raw !== '' && Number.isFinite(n) ? n : null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function openDetail(tipGameId: number) {
    const sp = new URLSearchParams(window.location.search);
    sp.set('tipGameId', String(tipGameId));
    window.history.pushState(null, '', `${window.location.pathname}?${sp.toString()}`);
    setSelectedId(tipGameId);
  }

  function backToList() {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('tipGameId');
    const qs = sp.toString();
    window.history.pushState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    setSelectedId(null);
  }

  if (selectedId != null) {
    return <TipovackaDetail tipGameId={selectedId} onBack={backToList} />;
  }
  return <TipovackaList onOpen={openDetail} />;
}

const TIPOVACKA_TABS: readonly SubTab[] = ['prehled', 'planovani'];

function TipovackaList({ onOpen }: { onOpen: (tipGameId: number) => void }) {
  const [tab, setTab] = useTabParam<SubTab>('tab', TIPOVACKA_TABS, 'prehled');
  const [items, setItems] = useState<ProduktyTipovackaItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [limit, setLimit] = useState('50');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    listProduktyTipovacky()
      .then((res) => !cancelled && setItems(res.items))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const value = { open: 0, scheduled: 0, closed: 0, settled: 0 };
    for (const item of items) {
      const key = item.status.toLowerCase() as keyof typeof value;
      if (key in value) value[key] += 1;
    }
    return value;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const result = items.filter((item) => {
      if (status !== 'all' && item.status.toLowerCase() !== status) return false;
      return !q || item.title.toLowerCase().includes(q);
    });
    return limit === 'all' ? result : result.slice(0, Number(limit));
  }, [items, status, query, limit]);

  const reset = () => { setStatus('all'); setLimit('50'); setQuery(''); };
  const tableRows = filtered.map((item) => ({ ...item, prize: mainPrize(item.prizeDescription, item.prizeTitle), detail: item.tipGameId }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'title', label: 'Zápas / otázka', width: 270, render: (_, row) => <button type="button" className="rondo-tip-link" onClick={() => onOpen(row.tipGameId)}><span className="rondo-tip-avatar"><Target size={16} aria-hidden="true" /></span><span><strong>{row.title}</strong><small>Tipovačka #{row.tipGameId}</small></span></button> },
    { key: 'market', label: 'Trh', width: 90 },
    { key: 'status', label: 'Status', width: 120, render: (_, row) => statusBadge(row.status) },
    { key: 'matchStartDt', label: 'Datum zápasu', width: 155, render: (_, row) => dateTimeFmt(row.matchStartDt) },
    { key: 'prize', label: 'Hlavní výhra', width: 170, render: (_, row) => row.prize ?? '—' },
    { key: 'betCount', label: 'Sázek', numeric: true, width: 90, render: (_, row) => row.betCount == null ? '—' : intFmt(row.betCount) },
    { key: 'winnersCount', label: 'Výherců', numeric: true, width: 90, render: (_, row) => row.winnersCount == null ? '—' : intFmt(row.winnersCount) },
    { key: 'detail', label: '', width: 45, sortable: false, render: (_, row) => <button type="button" className="rondo-arc-user-detail" aria-label={`Detail tipovačky ${row.title}`} onClick={() => onOpen(row.tipGameId)}><ChevronRight size={16} aria-hidden="true" /></button> },
  ];

  return (
    <section className="rondo-arc-users-list rondo-arc-tipovacka p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">PRODUKTY</div><h1>Tipovačka</h1><p>Zápasy, plánování a výsledky tipování</p></div></header>
      <ArcTabs label="Tipovačka" items={[{ value: 'prehled', label: `Přehled ${loading ? '' : intFmt(items.length)}` }, { value: 'planovani', label: 'Plánování' }]} value={tab} onChange={setTab} className="rondo-tip-tabs" />
      {tab === 'planovani' ? (
        <Card title="Plánování" description="Pro tuto část zatím nejsou dostupná data." className="rondo-tip-planning" />
      ) : <>
        <div className="rondo-arc-users-list-metrics">
          <MetricCard label="Otevřeno" value={counts.open} context="Přijímá tipy" />
          <MetricCard label="Naplánováno" value={counts.scheduled} context="Připravené zápasy" />
          <MetricCard label="Uzavřeno" value={counts.closed} context="Čeká na výsledek" />
          <MetricCard label="Vyhodnoceno" value={counts.settled} context="Dokončené tipování" />
        </div>
        <ServiceBar variant="seg" selects={[
          { label: 'Status', value: status, onChange: setStatus, options: [
            { value: 'all', label: 'Všechny' }, { value: 'scheduled', label: 'Naplánováno' },
            { value: 'open', label: 'Otevřeno' }, { value: 'closed', label: 'Uzavřeno' },
            { value: 'settled', label: 'Vyhodnoceno' },
          ] },
          { label: 'Limit', value: limit, onChange: setLimit, options: LIMIT_OPTIONS },
        ]} onReset={reset} search={query} onSearch={setQuery} searchPlaceholder="zápas / otázka…" />
        <div className="rondo-arc-users-list-table rondo-arc-tip-table">
          {error && <p role="alert" className="rondo-arc-users-error">{error}</p>}
          {loading ? <p className="rondo-arc-users-loading">Načítám tipovačky…</p> : <SortableDataTable rows={tableRows} columns={columns} rowKey="tipGameId" caption="Tipovačka Ronda" emptyMessage="Žádná tipovačka neodpovídá filtru." />}
        </div>
        <p className="rondo-tip-count">{intFmt(filtered.length)} zobrazených tipovaček</p>
      </>}
    </section>
  );
}

function TipovackaDetail({ tipGameId, onBack }: { tipGameId: number; onBack: () => void }) {
  const [data, setData] = useState<ProduktyTipovackaDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getProduktyTipovackaDetail(tipGameId)
      .then((value) => !cancelled && setData(value))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [tipGameId]);

  if (loading) return <section className="rondo-arc-tip-detail p1-arc-scope"><Button variant="secondary" size="sm" onClick={onBack}>Zpět na tipovačku</Button><PageState title="Načítám tipovačku" loading /></section>;
  if (error || !data) return <section className="rondo-arc-tip-detail p1-arc-scope"><Button variant="secondary" size="sm" onClick={onBack}>Zpět na tipovačku</Button><PageState title="Tipovačku se nepodařilo načíst" message={error ?? 'Tipovačka nenalezena.'} /></section>;
  const prize = mainPrize(data.prizeDescription, data.prizeTitle);
  const betRows = data.bets.map((bet, index) => ({ ...bet, rank: index + 1 }));
  type BetRow = (typeof betRows)[number];
  const betColumns: DataColumn<BetRow>[] = [
    { key: 'rank', label: '#', numeric: true, width: 55 },
    { key: 'userId', label: 'Uživatel', width: 250, numeric: false, render: (_, row) => <span className="rondo-tip-bettor"><strong>{row.name ?? `Hráč ${row.userId}`}</strong><small>{[row.email, `ID ${row.userId}`].filter(Boolean).join(' · ')}</small></span> },
    { key: 'answer', label: 'Tip', width: 160, render: (_, row) => row.answer ?? '—' },
    { key: 'createdAt', label: 'Čas sázky', width: 160, render: (_, row) => dateTimeFmt(row.createdAt) },
    { key: 'isCorrect', label: 'Správně?', width: 105, render: (_, row) => row.isCorrect == null ? '—' : row.isCorrect ? 'Ano' : 'Ne' },
  ];

  return (
    <section className="rondo-arc-tip-detail p1-arc-scope">
      <Button variant="secondary" size="sm" onClick={onBack}>Zpět na tipovačku</Button>
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">TIPOVAČKA · {data.market ?? '—'}</div><h1>{data.title ?? `Tipovačka #${data.tipGameId}`}</h1><p>{data.questionText && data.questionText !== data.title ? data.questionText : `${STATUS_LABEL[data.status?.toLowerCase() ?? ''] ?? data.status ?? 'Bez stavu'} · ID ${data.tipGameId}`}</p></div></header>
      <div className="rondo-arc-users-list-metrics">
        <MetricCard label="Všechny sázky" value={data.betCount ?? 0} context={data.betCount == null ? 'Údaj není dostupný' : 'Celkem'} />
        <MetricCard label="Výherci" value={data.winnerCount ?? 0} context={data.winnerCount == null ? 'Údaj není dostupný' : 'Celkem'} />
        <MetricCard label="Správné tipy" value={data.correctBets} context="Ve vzorku" />
        <MetricCard label="Zobrazený vzorek" value={data.bets.length} context="Sázek" />
      </div>
      <div className="rondo-tip-detail-panels">
        <Card title="Termíny" description={`Tipovačka #${data.tipGameId}`} className="rondo-tip-terms-card"><dl><div><dt>Zápas</dt><dd>{dateTimeFmt(data.matchStartDt)}</dd></div><div><dt>Uzávěrka sázek</dt><dd>{dateTimeFmt(data.betCloseDt)}</dd></div><div><dt>Losování</dt><dd>{dateTimeFmt(data.drawDt)}</dd></div></dl></Card>
        <Card title="Hlavní výhra" description={data.market ?? undefined} className="rondo-tip-prize-card"><strong>{prize ?? '—'}</strong><div>{statusBadge(data.status)}</div></Card>
      </div>
      <Card title="Sázky" description="Zobrazený vzorek" className="rondo-tip-bets-card"><SortableDataTable rows={betRows} columns={betColumns} rowKey={(row) => `${row.userId}-${row.rank}`} caption="Sázky tipovačky" emptyMessage="Žádné sázky ve vzorku." /></Card>
    </section>
  );
}
